<template>
  <div
    class="opportunity-temperature"
    :class="{ 'opportunity-temperature--disabled': disabled }"
    :title="disabled ? disabledReason : null"
  >
    <p :id="titleId" class="opportunity-temperature__title">
      {{ $t('OPPORTUNITY_DETAILS.TEMPERATURE.TITLE') }}
    </p>
    <div
      class="opportunity-temperature__options"
      role="radiogroup"
      :aria-labelledby="titleId"
    >
      <button
        v-for="temperature in temperatures"
        :key="temperature"
        type="button"
        role="radio"
        class="opportunity-temperature__option"
        :class="{ 'is-selected': temperature === value }"
        :aria-checked="temperature === value ? 'true' : 'false'"
        :disabled="disabled"
        :data-temperature="temperature"
        @click="onSelect(temperature)"
      >
        <opportunity-temperature-icon :temperature="temperature" :size="36" />
        <span class="opportunity-temperature__label">
          {{ $t(labelKey(temperature)) }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import {
  OPPORTUNITY_TEMPERATURES,
  DEFAULT_OPPORTUNITY_TEMPERATURE,
  temperatureLabelKey,
} from 'dashboard/helper/opportunityTemperatureHelper';
import OpportunityTemperatureIcon from './OpportunityTemperatureIcon.vue';

let instanceCount = 0;

export default {
  name: 'OpportunityTemperatureSelect',
  components: {
    OpportunityTemperatureIcon,
  },
  props: {
    value: {
      type: String,
      default: DEFAULT_OPPORTUNITY_TEMPERATURE,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledReason: {
      type: String,
      default: '',
    },
  },
  data() {
    instanceCount += 1;
    return { instanceId: instanceCount };
  },
  computed: {
    temperatures() {
      return OPPORTUNITY_TEMPERATURES;
    },
    titleId() {
      return `opportunity-temperature-title-${this.instanceId}`;
    },
  },
  methods: {
    labelKey(temperature) {
      return temperatureLabelKey(temperature);
    },
    onSelect(temperature) {
      if (this.disabled || temperature === this.value) {
        return;
      }

      this.$emit('input', temperature);
    },
  },
};
</script>

<style lang="scss" scoped>
.opportunity-temperature {
  background: transparent;
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  padding: var(--space-slab) var(--space-normal) var(--space-normal);
}

.opportunity-temperature__title {
  color: var(--s-900);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--space-slab);
}

.opportunity-temperature__options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-normal);
}

.opportunity-temperature__option {
  align-items: center;
  background: var(--b-300);
  border: 2px solid var(--b-400);
  border-radius: var(--border-radius-large);
  color: var(--b-900);
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  font-family: inherit;
  gap: var(--space-micro);
  height: 7.6rem;
  justify-content: center;
  margin: 0;
  padding: var(--space-small) var(--space-smaller);
  transition: background 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.1s ease;
  width: 7.6rem;

  .opportunity-temperature-icon {
    filter: saturate(0.75);
    opacity: 0.85;
    transition: filter 0.15s ease, opacity 0.15s ease;
  }

  &:hover:not(:disabled):not(.is-selected) {
    background: var(--b-200);
    border-color: var(--s-500);

    .opportunity-temperature-icon {
      filter: none;
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px var(--w-100);
    outline: none;
  }

  &.is-selected {
    background: var(--white);
    border-color: var(--c-500);
    box-shadow: 0 1px 3px rgba(34, 70, 155, 0.18);

    .opportunity-temperature-icon {
      filter: none;
      opacity: 1;
    }

    .opportunity-temperature__label {
      font-weight: var(--font-weight-bold);
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.opportunity-temperature__label {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-normal);
  line-height: 1.2;
}

.opportunity-temperature--disabled {
  .opportunity-temperature__option:not(.is-selected) {
    opacity: 0.55;
  }
}
</style>
